import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BotaoMenuMobile from '../components/common/BotaoMenuMobile';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import s from './FilaProducao.module.css';

const LABEL_ANTERIOR = { em_producao: 'Na Fila', pronto: 'Em Produção' };

const POLL_MS = 5000;

function elapsedStr(desde, agora) {
  const ms = Math.max(0, agora - new Date(desde).getTime());
  const min = Math.floor(ms / 60000);
  const seg = Math.floor((ms % 60000) / 1000);
  return `${String(min).padStart(2, '0')}:${String(seg).padStart(2, '0')}`;
}

const fmtHora = iso => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const LABEL_STATUS = { na_fila: 'Na fila', em_producao: 'Em produção', pronto: 'Pronto' };

// Nome do PRÓXIMO estágio — é isso que aparece no botão de avançar, não o
// estágio atual (a coluna já deixa claro onde o pedido está agora).
const LABEL_PROXIMO = { na_fila: 'Produção', em_producao: 'Pronto' };

// Card individual de um pedido. Clicar no card inteiro só seleciona (mostra
// os detalhes à direita) — achado real (2026-09-17): clique em qualquer
// parte do card avançando o estágio era sensível demais, avançava sem
// querer. Avançar agora só acontece pelo botão explícito no rodapé, com o
// nome do estágio seguinte ("Produção"/"Pronto"). Prioridade continua num
// botão à parte (🚩).
function Card({ pedido, agora, corClasse, onAvancar, onPrioridade, onSelecionar, selecionado }) {
  const numero = String(pedido.id).padStart(3, '0');
  const pronto = pedido.status === 'pronto';
  return (
    <div
      className={`${s.card} ${corClasse} ${selecionado ? s.cardSelecionado : ''}`}
      onClick={() => onSelecionar(pedido.id)}
    >
      <div className={s.cardTopo}>
        <span className={s.cardNumero}>{numero}</span>
        {pedido.cliente_nome && <span className={s.cardCliente}>{pedido.cliente_nome}</span>}
        {pronto ? (
          <span className={s.cardHora}>✅ {fmtHora(pedido.pronto_em)} — levou {elapsedStr(pedido.criado_em, new Date(pedido.pronto_em).getTime())}</span>
        ) : (
          <>
            <span className={s.cardHora}>↑ {fmtHora(pedido.criado_em)}</span>
            <span className={s.cardCronometro}>🕐 {elapsedStr(pedido.criado_em, agora)}</span>
          </>
        )}
      </div>
      <div className={s.cardItens}>
        {pedido.itens.map((it, i) => (
          <div key={i} className={s.cardItem}>
            {Number(it.quantidade)}x&nbsp;{it.descricao}
          </div>
        ))}
      </div>
      <div className={s.cardRodape}>
        <span className={s.cardMesa}>🧾 {pedido.mesa_label || `Comanda ${pedido.comanda_id ?? pedido.id}`}</span>
        <div className={s.cardAcoes}>
          <button
            type="button"
            className={`${s.btnFlag} ${pedido.prioridade ? s.btnFlagAtiva : ''}`}
            title={pedido.prioridade ? 'Tirar prioridade' : 'Marcar como prioridade'}
            onClick={e => { e.stopPropagation(); onPrioridade(pedido.id); }}
          >🚩</button>
          {pronto ? (
            <span className={`${s.badge} ${corClasse}`}>{LABEL_STATUS.pronto}</span>
          ) : (
            <button
              type="button"
              className={`${s.btnAvancarCard} ${corClasse}`}
              onClick={e => { e.stopPropagation(); onAvancar(pedido.id); }}
            >{LABEL_PROXIMO[pedido.status]} →</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FilaProducao() {
  const { logout } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [busca, setBusca] = useState('');
  const [somAtivado, setSomAtivado] = useState(true);
  const [agora, setAgora] = useState(Date.now());
  const [novoPedidoAviso, setNovoPedidoAviso] = useState(0);
  const [erro, setErro] = useState('');
  const buscaRef = useRef(null);
  const idsConhecidos = useRef(new Set());
  const audioCtxRef = useRef(null);

  // Bipe curto sintetizado na hora (Web Audio API) — o projeto não tem
  // nenhum arquivo de som pra reaproveitar, e um áudio pré-gravado de
  // verdade infla o bundle à toa por um "tim" de 2 notas.
  const tocarBeep = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = audioCtxRef.current || (audioCtxRef.current = new Ctx());
      [880, 1175].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.001, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + i * 0.12 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.15);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.12);
        osc.stop(ctx.currentTime + i * 0.12 + 0.16);
      });
    } catch { /* navegador sem suporte ou áudio bloqueado — some silenciosamente */ }
  }, []);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/fila-producao');
      // Toca som/mostra aviso só quando aparece um pedido REALMENTE novo
      // (id nunca visto nesta sessão) — evita disparar de novo a cada
      // polling só porque o pedido continua na lista.
      const idsAtuais = new Set(data.map(p => p.id));
      if (idsConhecidos.current.size > 0) {
        const novos = data.filter(p => !idsConhecidos.current.has(p.id));
        if (novos.length) {
          setNovoPedidoAviso(n => n + novos.length);
          if (somAtivado) tocarBeep();
        }
      }
      idsConhecidos.current = idsAtuais;
      setPedidos(data);
      setErro('');
    } catch {
      setErro('Falha ao atualizar a fila — tentando de novo em instantes.');
    }
  }, [somAtivado, tocarBeep]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    const t = setInterval(carregar, POLL_MS);
    return () => clearInterval(t);
  }, [carregar]);
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'F3') { e.preventDefault(); buscaRef.current?.focus(); }
      else if (e.key === 'F5') { e.preventDefault(); carregar(); }
      else if (e.key === 'Delete') { e.preventDefault(); limparFinalizados(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [carregar]); // eslint-disable-line

  const avancar = async id => {
    try {
      const { data } = await api.post(`/fila-producao/${id}/avancar`);
      setPedidos(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
    } catch { setErro('Não foi possível avançar este pedido.'); }
  };

  // Desfaz um avanço por engano (só correção pontual, exige admin/gerente
  // — mesmo padrão de senha usado no resto do sistema, ver MesasMobile.jsx
  // cancelarItemSelecionado). Pede a senha já na 1ª tentativa aproveitando o
  // 422 que o backend sempre devolve sem ela, com o texto de erro certo.
  const voltar = async (id, senha_admin) => {
    try {
      const { data } = await api.post(`/fila-producao/${id}/voltar`, senha_admin ? { senha_admin } : undefined);
      setPedidos(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
    } catch (err) {
      if (err.response?.data?.exige_senha_admin) {
        const senha = await pedirSenhaAdmin(err.response.data.erro, {
          acao: 'voltar_estagio_fila_producao', detalhes: `pedido #${String(id).padStart(3, '0')}`,
        });
        if (senha === null) return;
        return voltar(id, senha);
      }
      setErro(err.response?.data?.erro || 'Não foi possível voltar este pedido.');
    }
  };

  const alternarPrioridade = async id => {
    try {
      const { data } = await api.post(`/fila-producao/${id}/prioridade`);
      setPedidos(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
    } catch { setErro('Não foi possível alterar a prioridade deste pedido.'); }
  };

  const limparFinalizados = async () => {
    try {
      await api.post('/fila-producao/limpar-finalizados');
      setSelecionado(null);
      carregar();
    } catch { setErro('Não foi possível limpar os pedidos finalizados.'); }
  };

  const buscaNorm = busca.trim().toLowerCase();
  const bate = p => !buscaNorm
    || String(p.id).includes(buscaNorm)
    || (p.mesa_label || '').toLowerCase().includes(buscaNorm);

  const filtrados = pedidos.filter(bate);
  const prioridade  = filtrados.filter(p => p.prioridade && p.status !== 'pronto');
  const naFila      = filtrados.filter(p => p.status === 'na_fila' && !p.prioridade);
  const emProducao  = filtrados.filter(p => p.status === 'em_producao' && !p.prioridade);
  // "Prontos" é a única coluna com o mais recente no topo — quem tá vendo
  // essa coluna quer saber o que ACABOU de sair, não vasculhar por baixo de
  // pedidos prontos há muito tempo (as outras colunas continuam FIFO: quem
  // espera há mais tempo aparece primeiro).
  const prontos = filtrados.filter(p => p.status === 'pronto')
    .sort((a, b) => new Date(b.pronto_em) - new Date(a.pronto_em));

  const detalhe = pedidos.find(p => p.id === selecionado) || null;

  return (
    <div className={s.pagina}>
      <header className={s.header}>
        <div className={s.headerTitulo}>
          <span className={s.headerIcone}>👨‍🍳</span>
          <div>
            <h1>Fila de Produção</h1>
            <p>Acompanhe e finalize os pedidos da cozinha</p>
          </div>
        </div>
        <div className={s.headerDireita}>
          <BotaoMenuMobile />
          <span className={s.statusOnline}>🟢 Cozinha Online</span>
          <span className={s.relogio}>{new Date(agora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
          <button type="button" className={s.btnSair} onClick={logout}>Sair</button>
        </div>
      </header>

      {erro && <div className={s.avisoErro}>{erro}</div>}

      <div className={s.statsRow}>
        <div className={`${s.statCard} ${s.statAzul}`}><span className={s.statValor}>{pedidos.length}</span><span>Total de Pedidos</span></div>
        <div className={`${s.statCard} ${s.statAmarelo}`}><span className={s.statValor}>{naFila.length}</span><span>Na Fila</span></div>
        <div className={`${s.statCard} ${s.statLaranja}`}><span className={s.statValor}>{emProducao.length}</span><span>Em Produção</span></div>
        <div className={`${s.statCard} ${s.statVerde}`}><span className={s.statValor}>{prontos.length}</span><span>Prontos</span></div>
        <div className={`${s.statCard} ${s.statVermelho}`}><span className={s.statValor}>{prioridade.length}</span><span>Prioridade</span></div>
        <input
          ref={buscaRef}
          className={s.busca}
          placeholder="Buscar comanda... (F3)"
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className={s.corpo}>
        <div className={s.colunas}>
          <Coluna titulo="NA FILA" sub="Pedidos recebidos, aguardando produção" corClasse={s.corAmarela} lista={naFila}
            agora={agora} onAvancar={avancar} onPrioridade={alternarPrioridade} onSelecionar={setSelecionado} selecionado={selecionado} />
          <Coluna titulo="EM PRODUÇÃO" sub="Pedidos em andamento na cozinha" corClasse={s.corLaranja} lista={emProducao}
            agora={agora} onAvancar={avancar} onPrioridade={alternarPrioridade} onSelecionar={setSelecionado} selecionado={selecionado} />
          <Coluna titulo="PRONTOS" sub="Pedidos finalizados pela cozinha" corClasse={s.corVerde} lista={prontos}
            agora={agora} onAvancar={avancar} onPrioridade={alternarPrioridade} onSelecionar={setSelecionado} selecionado={selecionado} />
          <Coluna titulo="PRIORIDADE" sub="Pedido requer atenção" corClasse={s.corVermelha} lista={prioridade}
            agora={agora} onAvancar={avancar} onPrioridade={alternarPrioridade} onSelecionar={setSelecionado} selecionado={selecionado} />
        </div>

        <aside className={s.painelLateral}>
          <div className={s.painelBloco}>
            <h3>Detalhes do Pedido</h3>
            {!detalhe && <p className={s.painelVazio}>Clique num pedido pra ver os detalhes aqui.</p>}
            {detalhe && (
              <div>
                <div className={s.detalheTopo}>
                  <span className={s.cardNumero}>{String(detalhe.id).padStart(3, '0')}</span>
                  {detalhe.cliente_nome && <span className={s.cardCliente}>{detalhe.cliente_nome}</span>}
                  <span className={`${s.badge} ${
                    detalhe.prioridade && detalhe.status !== 'pronto' ? s.corVermelha
                      : detalhe.status === 'na_fila' ? s.corAmarela
                      : detalhe.status === 'em_producao' ? s.corLaranja : s.corVerde
                  }`}>{detalhe.prioridade && detalhe.status !== 'pronto' ? 'Prioridade' : LABEL_STATUS[detalhe.status]}</span>
                </div>
                <p className={s.detalheMesa}>🧾 {detalhe.mesa_label || `Comanda ${detalhe.comanda_id ?? detalhe.id}`}</p>
                <p className={s.detalheHora}>Recebido às {fmtHora(detalhe.criado_em)} — {elapsedStr(detalhe.criado_em, agora)} na fila</p>
                <div className={s.detalheItens}>
                  {detalhe.itens.map((it, i) => (
                    <div key={i} className={s.detalheItem}>
                      <strong>{Number(it.quantidade)}x</strong> {it.descricao}
                      {it.observacao && <div className={s.detalheObs}>📝 {it.observacao}</div>}
                    </div>
                  ))}
                </div>
                {detalhe.status !== 'pronto' && (
                  <button type="button" className={s.btnAvancar} onClick={() => avancar(detalhe.id)}>
                    {detalhe.status === 'na_fila' ? '▶ Iniciar Produção' : '✓ Marcar como Pronto'}
                  </button>
                )}
                {detalhe.status !== 'pronto' && (
                  <button type="button" className={s.btnPrioridadeToggle} onClick={() => alternarPrioridade(detalhe.id)}>
                    {detalhe.prioridade ? '🚩 Tirar prioridade' : '🚩 Marcar prioridade'}
                  </button>
                )}
                {LABEL_ANTERIOR[detalhe.status] && (
                  <button type="button" className={s.btnVoltar} onClick={() => voltar(detalhe.id)}
                    title="Exige senha de administrador ou gerente — use só se avançou por engano">
                    ↩ Voltar pra {LABEL_ANTERIOR[detalhe.status]}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className={s.painelBloco}>
            <h3>Legenda de Status</h3>
            <div className={s.legendaLinha}><span className={`${s.legendaBolinha} ${s.corAmarela}`} /> <div><strong>Na fila</strong><br />Pedido recebido, aguardando produção</div></div>
            <div className={s.legendaLinha}><span className={`${s.legendaBolinha} ${s.corLaranja}`} /> <div><strong>Em produção</strong><br />Em andamento na cozinha</div></div>
            <div className={s.legendaLinha}><span className={`${s.legendaBolinha} ${s.corVerde}`} /> <div><strong>Pronto</strong><br />Pedido concluído na cozinha</div></div>
            <div className={s.legendaLinha}><span className={`${s.legendaBolinha} ${s.corVermelha}`} /> <div><strong>Prioridade</strong><br />Requer atenção imediata</div></div>
          </div>
        </aside>
      </div>

      <footer className={s.footer}>
        <button type="button" className={s.footerBotao} onClick={() => setNovoPedidoAviso(0)}>
          🔔 Novo pedido {novoPedidoAviso > 0 && <span className={s.footerBadge}>{novoPedidoAviso}</span>}
        </button>
        <button type="button" className={s.footerBotao} onClick={() => setSomAtivado(v => !v)}>
          {somAtivado ? '🔊' : '🔇'} Som: {somAtivado ? 'Ativado' : 'Desativado'}
        </button>
        <button type="button" className={s.footerBotao} onClick={carregar}>🔄 Atualizar <span className={s.footerTecla}>F5</span></button>
        <button type="button" className={s.footerBotao} onClick={() => buscaRef.current?.focus()}>🔍 Buscar <span className={s.footerTecla}>F3</span></button>
        <button type="button" className={`${s.footerBotao} ${s.footerBotaoPerigo}`} onClick={limparFinalizados}>
          🗑 Limpar Finalizados <span className={s.footerTecla}>Del</span>
        </button>
      </footer>
    </div>
  );
}

function Coluna({ titulo, sub, corClasse, lista, agora, onAvancar, onPrioridade, onSelecionar, selecionado }) {
  return (
    <div className={`${s.coluna} ${corClasse}`}>
      <div className={s.colunaHeader}>
        <div>
          <strong>{titulo}</strong>
          <span className={s.colunaContagem}>{lista.length}</span>
        </div>
        <small>{sub}</small>
      </div>
      <div className={s.colunaCorpo}>
        {lista.length === 0 && <p className={s.colunaVazia}>Nenhum pedido aqui.</p>}
        {lista.map(p => (
          <Card key={p.id} pedido={p} agora={agora} corClasse={corClasse}
            onAvancar={onAvancar} onPrioridade={onPrioridade} onSelecionar={onSelecionar}
            selecionado={selecionado === p.id} />
        ))}
      </div>
    </div>
  );
}
